import { useState } from 'react';
import { Pencil, Trash2 } from 'lucide-react';
import type { InboundTrackingEntry } from '@/apis/warehouse';
import { useAuthStore } from '@/stores/authStore';
import { useDeleteInboundTracking } from '@/hooks/useWarehouse';
import { pairTrackingEntries, TRACKING_ROW_STATUS_STYLE, canActOnTrackingEntry } from './warehouse-shared';

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

interface SideCellProps {
  entry: InboundTrackingEntry | null;
  orderId: string;
  canEdit: boolean;
  onEdit: (entry: InboundTrackingEntry) => void;
}

function SideCells({ entry, orderId, canEdit, onEdit }: SideCellProps) {
  const deleteTracking = useDeleteInboundTracking(orderId);

  if (!entry) {
    return (
      <>
        <td className="px-3 py-3 text-sm text-lightGray">-</td>
        <td className="px-3 py-3 text-sm text-lightGray">-</td>
        <td className="px-3 py-3 text-sm text-lightGray">-</td>
      </>
    );
  }

  return (
    <>
      <td className="whitespace-nowrap px-3 py-3 text-sm text-black">{formatDate(entry.createdAt)}</td>
      <td className="whitespace-nowrap px-3 py-3 text-sm text-black">{entry.courierOrSupplierName}</td>
      <td className="px-3 py-3 text-sm text-black">
        <div className="flex items-center gap-2">
          <span>{entry.trackingNumber || '-'}</span>
          {canEdit && (
            <div className="ml-auto flex items-center gap-1 text-lightGray">
              <button
                type="button"
                onClick={() => onEdit(entry)}
                className="hover:text-primary-s500"
                aria-label="Edit tracking entry"
              >
                <Pencil size={13} />
              </button>
              <button
                type="button"
                onClick={() => deleteTracking.mutate(entry.id)}
                disabled={deleteTracking.isPending}
                className="hover:text-danger disabled:opacity-50"
                aria-label="Delete tracking entry"
              >
                <Trash2 size={13} />
              </button>
            </div>
          )}
        </div>
      </td>
    </>
  );
}

interface Props {
  orderId: string;
  entries: InboundTrackingEntry[];
  onEditEntry: (entry: InboundTrackingEntry) => void;
}

export function TrackingComparisonTable({ orderId, entries, onEditEntry }: Props) {
  const currentUser = useAuthStore((s) => s.user);
  const rows = pairTrackingEntries(entries);

  if (rows.length === 0) {
    return <p className="px-4 py-6 text-center text-sm text-lightGray">No inbound tracking yet</p>;
  }

  return (
    <div className="overflow-x-auto rounded-2xl border border-smoke">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-smoke bg-smoke1">
            <th colSpan={3} className="border-r border-smoke px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-lightGray">
              Sales Team Input
            </th>
            <th colSpan={3} className="border-r border-smoke px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-lightGray">
              Warehouse Scan Input
            </th>
            <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-lightGray">Status</th>
          </tr>
          <tr className="border-b border-smoke bg-smoke1">
            {['Date', 'Carrier', 'Tracking#', 'Date', 'Carrier', 'Tracking#'].map((col, i) => (
              <th
                key={`${col}-${i}`}
                className={`px-3 py-1.5 text-left text-[11px] font-medium text-lightGray ${i === 2 ? 'border-r border-smoke' : ''}`}
              >
                {col}
              </th>
            ))}
            <th />
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            const style = TRACKING_ROW_STATUS_STYLE[row.status];
            return (
              <tr key={row.id} className="border-b border-smoke last:border-0">
                <SideCells
                  entry={row.sales}
                  orderId={orderId}
                  canEdit={!!row.sales && canActOnTrackingEntry(currentUser, row.sales)}
                  onEdit={onEditEntry}
                />
                <SideCells
                  entry={row.warehouse}
                  orderId={orderId}
                  canEdit={!!row.warehouse && canActOnTrackingEntry(currentUser, row.warehouse)}
                  onEdit={onEditEntry}
                />
                <td className="px-3 py-3">
                  <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${style.bg}`}>{style.label}</span>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
